import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Stack, CircularProgress, IconButton, Dialog,
  alpha, Pagination
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import axios from 'axios';

import config from '../config';

const API_URL = `${config.API_BASE_URL}`;

const ITEMS_PER_PAGE = 6;
const AUTO_SLIDE_INTERVAL = 5000;

export default function DesignGallery() {
  // ─── Slider State (last 6) ───
  const [sliderImages, setSliderImages] = useState([]);
  const [sliderLoading, setSliderLoading] = useState(true);
  const [sliderIndex, setSliderIndex] = useState(0);
  const sliderTimer = useRef(null);
  const sliderRef = useRef(null);

  // ─── Grid State (paginated) ───
  const [gridImages, setGridImages] = useState([]);
  const [gridLoading, setGridLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // ─── Lightbox Viewer ───
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerImages, setViewerImages] = useState([]);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [zoom, setZoom] = useState(1);

  // ─── Init ───
  useEffect(() => {
    fetchSliderImages();
    fetchGridImages(1);
    return () => {
      if (sliderTimer.current) clearInterval(sliderTimer.current);
    };
  }, []);

  // ─── Fetch last 6 for slider ───
  const fetchSliderImages = async () => {
    try {
      setSliderLoading(true);
      const response = await axios.get(
        `${API_URL}/DesignSubscriptions/gallery/sent/all`,
        { params: { page: 1, limit: 6 } }
      );
      setSliderImages(response.data?.images || []);
    } catch (err) {
      console.error('Slider fetch error:', err);
    } finally {
      setSliderLoading(false);
    }
  };

  // ─── Fetch paginated grid ───
  const fetchGridImages = async (page) => {
    try {
      setGridLoading(true);
      const response = await axios.get(
        `${API_URL}/DesignSubscriptions/gallery/sent/all`,
        { params: { page, limit: ITEMS_PER_PAGE } }
      );
      const data = response.data;
      setGridImages(data?.images || []);
      setCurrentPage(data?.page || 1);
      setTotalPages(data?.totalPages || 1);
    } catch (err) {
      console.error('Grid fetch error:', err);
    } finally {
      setGridLoading(false);
    }
  };

  // ─── Auto Slider (5s) ───
  useEffect(() => {
    if (sliderImages.length <= 1) return;
    if (sliderTimer.current) clearInterval(sliderTimer.current);

    sliderTimer.current = setInterval(() => {
      setSliderIndex((prev) => (prev + 1) % sliderImages.length);
    }, AUTO_SLIDE_INTERVAL);

    return () => {
      if (sliderTimer.current) clearInterval(sliderTimer.current);
    };
  }, [sliderImages.length]);

  // ─── Manual slider nav ───
  const goSliderPrev = () => {
    setSliderIndex((prev) => (prev - 1 + sliderImages.length) % sliderImages.length);
  };
  const goSliderNext = () => {
    setSliderIndex((prev) => (prev + 1) % sliderImages.length);
  };

  // ─── Page Change ───
  const handlePageChange = (_, page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
      fetchGridImages(page);
    }
  };

  // ─── Open Viewer ───
  const openViewer = (images, index) => {
    setViewerImages(images.map((img) => img.ImageUrl));
    setViewerIndex(index);
    setZoom(1);
    setViewerOpen(true);
  };

  const closeViewer = () => {
    setViewerOpen(false);
    setZoom(1);
  };

  const viewerPrev = () => {
    setZoom(1);
    setViewerIndex((prev) => (prev - 1 + viewerImages.length) % viewerImages.length);
  };

  const viewerNext = () => {
    setZoom(1);
    setViewerIndex((prev) => (prev + 1) % viewerImages.length);
  };

  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.5, 4));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.5, 0.5));

  // ─── Keyboard nav in viewer ───
  useEffect(() => {
    if (!viewerOpen) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') viewerPrev();
      else if (e.key === 'ArrowRight') viewerNext();
      else if (e.key === 'Escape') closeViewer();
      else if (e.key === '+' || e.key === '=') handleZoomIn();
      else if (e.key === '-') handleZoomOut();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [viewerOpen, viewerImages.length]);

  // ==================== RENDER: Loading ====================
  if (sliderLoading && gridLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <CircularProgress size={40} sx={{ color: '#ad1457' }} />
      </Box>
    );
  }

  // ==================== RENDER: Empty ====================
  if (sliderImages.length === 0 && gridImages.length === 0) {
    return (
      <Box textAlign="center" py={6}>
        <Typography variant="body1" color="text.secondary">
          لا توجد تصاميم بعد
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* ─── Title ─── */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={2}
        sx={{ pt: 4, pb: 3, px: 2 }}
      >
        <Box
          sx={{
            flex: 1,
            maxWidth: 80,
            height: 2,
            borderRadius: 1,
            background: `linear-gradient(90deg, transparent, ${alpha('#ad1457', 0.3)})`,
          }}
        />
        <Stack direction="row" alignItems="center" spacing={1}>
          <AutoAwesomeIcon sx={{ color: '#ad1457', fontSize: 28 }} />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 900,
              color: '#ad1457',
              letterSpacing: 0.5,
              fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.6rem' },
            }}
          >
            معرض الأعمال
          </Typography>
          <AutoAwesomeIcon sx={{ color: '#ad1457', fontSize: 28 }} />
        </Stack>
        <Box
          sx={{
            flex: 1,
            maxWidth: 80,
            height: 2,
            borderRadius: 1,
            background: `linear-gradient(90deg, ${alpha('#ad1457', 0.3)}, transparent)`,
          }}
        />
      </Stack>

      {/* ─── Slider (last 6) ─── */}
      {sliderImages.length > 0 && (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: { xs: 220, sm: 280, md: 340 },
            borderRadius: 3,
            overflow: 'hidden',
            mb: 3,
            boxShadow: `0 4px 20px ${alpha('#000', 0.1)}`,
          }}
        >
          {/* Image */}
          <Box
            component="img"
            src={sliderImages[sliderIndex]?.ImageUrl}
            alt={`Slide ${sliderIndex + 1}`}
            onClick={() => openViewer(sliderImages, sliderIndex)}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              cursor: 'pointer',
              transition: 'opacity 0.6s ease-in-out',
            }}
          />

          {/* Gradient overlay */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 80,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.4))',
              pointerEvents: 'none',
            }}
          />

          {/* Left Arrow */}
          {sliderImages.length > 1 && (
            <IconButton
              onClick={goSliderPrev}
              sx={{
                position: 'absolute',
                top: '50%',
                left: 12,
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.85)',
                backdropFilter: 'blur(4px)',
                width: 40,
                height: 40,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}

          {/* Right Arrow */}
          {sliderImages.length > 1 && (
            <IconButton
              onClick={goSliderNext}
              sx={{
                position: 'absolute',
                top: '50%',
                right: 12,
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.85)',
                backdropFilter: 'blur(4px)',
                width: 40,
                height: 40,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          )}

          {/* Dots */}
          <Stack
            direction="row"
            spacing={0.8}
            justifyContent="center"
            sx={{
              position: 'absolute',
              bottom: 14,
              left: 0,
              right: 0,
            }}
          >
            {sliderImages.map((_, i) => (
              <Box
                key={i}
                onClick={() => setSliderIndex(i)}
                sx={{
                  width: sliderIndex === i ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: sliderIndex === i ? '#fff' : 'rgba(255,255,255,0.45)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* ─── Grid (6 per page) ─── */}
      {gridLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={28} sx={{ color: '#ad1457' }} />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
            },
            gap: 1,
          }}
        >
          {gridImages.map((item, index) => (
            <Box
              key={`grid-${item.Id}`}
              onClick={() => openViewer(gridImages, index)}
              sx={{
                position: 'relative',
                paddingTop: '100%', // square
                borderRadius: 2,
                overflow: 'hidden',
                cursor: 'pointer',
                '&:hover .grid-overlay': {
                  opacity: 1,
                },
                '&:hover img': {
                  transform: 'scale(1.05)',
                },
              }}
            >
              <Box
                component="img"
                src={item.ImageUrl}
                alt={`Design ${item.Id}`}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.3s ease',
                }}
              />
              {/* Hover overlay */}
              <Box
                className="grid-overlay"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: 'rgba(173, 20, 87, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                }}
              >
                <ZoomInIcon sx={{ color: '#fff', fontSize: 36 }} />
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* ─── Pagination ─── */}
      {totalPages > 1 && (
        <Stack alignItems="center" sx={{ pt: 3, pb: 1 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            size="medium"
            shape="rounded"
            sx={{
              '& .MuiPaginationItem-root': {
                fontWeight: 600,
                '&.Mui-selected': {
                  bgcolor: '#ad1457',
                  color: '#fff',
                  '&:hover': { bgcolor: '#880e4f' },
                },
              },
            }}
          />
        </Stack>
      )}

      {/* ─── Full Screen Lightbox Viewer ─── */}
      <Dialog
        open={viewerOpen}
        onClose={closeViewer}
        maxWidth={false}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0,0,0,0.95)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Viewer Top Bar */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1,
            bgcolor: 'rgba(0,0,0,0.5)',
          }}
        >
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
            {viewerIndex + 1} / {viewerImages.length}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <IconButton onClick={handleZoomOut} sx={{ color: '#fff' }}>
              <ZoomOutIcon />
            </IconButton>
            <Typography
              variant="body2"
              sx={{
                color: '#fff',
                minWidth: 50,
                textAlign: 'center',
                lineHeight: '40px',
                fontWeight: 600,
              }}
            >
              {Math.round(zoom * 100)}%
            </Typography>
            <IconButton onClick={handleZoomIn} sx={{ color: '#fff' }}>
              <ZoomInIcon />
            </IconButton>
            <IconButton onClick={closeViewer} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>

        {/* Viewer Image */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'auto',
          }}
        >
          {/* Left Arrow */}
          {viewerImages.length > 1 && (
            <IconButton
              onClick={viewerPrev}
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.1)',
                color: '#fff',
                width: 48,
                height: 48,
                zIndex: 2,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 32 }} />
            </IconButton>
          )}

          <Box
            component="img"
            src={viewerImages[viewerIndex]}
            alt={`View ${viewerIndex + 1}`}
            sx={{
              maxWidth: '90%',
              maxHeight: '85vh',
              objectFit: 'contain',
              transform: `scale(${zoom})`,
              transition: 'transform 0.2s ease',
              borderRadius: 1,
              userSelect: 'none',
            }}
            onDoubleClick={() => setZoom((z) => (z === 1 ? 2 : 1))}
          />

          {/* Right Arrow */}
          {viewerImages.length > 1 && (
            <IconButton
              onClick={viewerNext}
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.1)',
                color: '#fff',
                width: 48,
                height: 48,
                zIndex: 2,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 32 }} />
            </IconButton>
          )}
        </Box>

        {/* Viewer Thumbnails */}
        {viewerImages.length > 1 && (
          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            sx={{
              py: 1.5,
              px: 2,
              bgcolor: 'rgba(0,0,0,0.5)',
              overflowX: 'auto',
            }}
          >
            {viewerImages.map((url, i) => (
              <Box
                key={i}
                onClick={() => {
                  setViewerIndex(i);
                  setZoom(1);
                }}
                component="img"
                src={url}
                alt={`Thumb ${i + 1}`}
                sx={{
                  width: 56,
                  height: 56,
                  objectFit: 'cover',
                  borderRadius: 1,
                  cursor: 'pointer',
                  border: viewerIndex === i ? '2px solid #ad1457' : '2px solid transparent',
                  opacity: viewerIndex === i ? 1 : 0.5,
                  transition: 'all 0.2s',
                  flexShrink: 0,
                  '&:hover': { opacity: 0.8 },
                }}
              />
            ))}
          </Stack>
        )}
      </Dialog>
    </Box>
  );
}